import React, {ChangeEvent, useContext, useEffect, useState} from 'react';
import { useAsyncResource } from 'use-async-resource';
import { checkForItemsInInventory } from '../../utils/warehouses-hooks';
import { MissingItemsForTasks } from '../../pages/tasks/MissingItemsForTasks';
import Select from 'react-select';
import { InventoryContent } from '../../models/warehouse-models';
import { useTranslation } from 'react-i18next';
import { optionSelectStyles } from '../../styles/selectStyles';
import { TasksContext } from '../../context/TasksContext';
import { calcOilPercentage } from '../../pages/stock/InventoryInner';
import { StepWizardChildProps } from 'react-step-wizard';
import { Button } from 'react-bootstrap';

export const SelectInventoryItemsStep: React.FC<Partial<StepWizardChildProps>> = ({ previousStep, nextStep }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tTable } = useTranslation('translation', { keyPrefix: 'table' });
    const { t: tRequest } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const { selectedTask, usedItems, setUsedItems, taskDevices, materials } = useContext(TasksContext);
    const neededItems = selectedTask?.neededItems ?? [];
    const userId = selectedTask?.assigneeId ?? -1;
    const [getInventoryCheckup] = useAsyncResource(checkForItemsInInventory, userId, neededItems);
    const { missing, available } = getInventoryCheckup();

    const selectInventoryItem = (
        defaultCount: number,
        defaultAmount: number,
        newValue: InventoryContent | null,
        neededItemIndex: number
    ) => {
        if (newValue)
            setUsedItems((prevState) => {
                prevState[neededItemIndex] = { ...newValue, count: defaultCount, availableAmount: defaultAmount };
                return [...prevState];
            });
    };

    const handleItemSelection = (
        defaultCount: number,
        defaultAmount: number,
        newValue: InventoryContent | null,
        neededItemIndex: number
    ) => {
        if (newValue) {
            selectInventoryItem(defaultCount, defaultAmount, newValue, neededItemIndex);
            setDefaultValues((prevDefaultValues) => ({
                ...(prevDefaultValues || {}),
                [neededItemIndex]: newValue.idOfPassport,
            }));
        }
    };

    const onChangeCount = (newCount: number, neededItemsIndex: number) => {
        setUsedItems((prevState) => {
            prevState[neededItemsIndex] = { ...prevState[neededItemsIndex], count: newCount };
            return [...prevState];
        });
    };

    const onChangeOil = (newQuantity: number, neededItemsIndex: number) => {
        setUsedItems((prevState) => {
            prevState[neededItemsIndex] = { ...prevState[neededItemsIndex], availableAmount: newQuantity };
            return [...prevState];
        });
    };

    const [defaultValues, setDefaultValues] = useState<{ [key: number]: number } | undefined>();

    const checkDisabled = () => {
        return (
            missing?.length > 0 ||
            !defaultValues
        );
    };

    useEffect(() => {
        neededItems.forEach(({ itemId, itemName, itemType, itemCount, oilQuantity }, neededItemIndex) => {
            const defaultValue = available.find((item) => itemId === item.idOfPassport);
            if (defaultValue) {
                handleItemSelection(itemCount, oilQuantity, defaultValue, neededItemIndex);
            }
        });
    }, [neededItems, available]);

    return (
        <div className='w-100 h-100 p-3'>
            <div className='scrollable-container w-100'>
                <div className='d-flex justify-content-around w-100 mb-3 '>
                    <h5>{t('neededItems')}</h5>
                    <h5>{t('inventory')}</h5>
                </div>
                <table className='w-100 h-100 mb-2'>
                    <thead>
                        <tr className='w-100 text-center fw-bold '>
                            <th>{t('materials')}</th>
                            <th>{t('type')}</th>
                            <th>{t('quantity')}</th>
                            <th>{tRequest('chooseAvailable')}</th>
                            <th>{tTable('selectedQuantity')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {neededItems.map(({ itemId, itemName, itemType, itemCount, oilQuantity }, neededItemIndex) => {
                            return (
                                <tr key={'item' + neededItemIndex} className='w-100 text-center '>
                                    <td className='pt-2'>{itemName}</td>
                                    <td className='pt-2'>{t(itemType.toLowerCase())}</td>
                                    <td className='pt-2'>{itemCount}</td>
                                    <td className='pt-2 border-start border-secondary ps-5'>
                                        <Select<InventoryContent, false>
                                            styles={optionSelectStyles}
                                            onChange={(newValue) => {
                                                handleItemSelection(itemCount, oilQuantity, newValue, neededItemIndex);
                                            }}
                                            getOptionLabel={({ batchCode, passport, availableAmount, count }) =>
                                                `${passport} : ${batchCode} ${
                                                    availableAmount
                                                        ? '(' + availableAmount + 'ml)'
                                                        : '(x' + count + ')'
                                                } `
                                            }
                                            getOptionValue={({ id }) => `${id}`}
                                            options={available.filter((item) => itemId === item.idOfPassport)}
                                            value={available.find((item) => itemId === item.idOfPassport)}
                                        />
                                    </td>
                                    {oilQuantity ? (
                                        <td className='pt-2'>
                                            {usedItems &&
                                                oilSliderEditable(
                                                    oilQuantity,
                                                    usedItems[neededItemIndex]?.availableAmount,
                                                    oilQuantity,
                                                    (e) => onChangeOil(+e.target.value, neededItemIndex)
                                                )}
                                        </td>
                                    ) : (
                                        <td className='pt-2'>
                                            <input
                                                type='number'
                                                onChange={(e) => onChangeCount(+e.target.value, neededItemIndex)}
                                                value={itemCount}
                                                min={1}
                                                max={itemCount}
                                            />
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <MissingItemsForTasks missingItems={missing} userId={userId + ''} />
            </div>
            <div className='d-flex mt-5 w-100 justify-content-between align-self-end'>
                <Button onClick={() => previousStep!()}>{t('previous')}</Button>
                <Button disabled={checkDisabled()} onClick={() => nextStep!()}>
                    {t('next')}
                </Button>
            </div>
        </div>
    );
};

export const oilSliderEditable = (
    defaultValue: number,
    selectedAmount?: number,
    totalAmountNeeded?: number,
    onChange: (e: ChangeEvent<HTMLInputElement>) => void = () => {}
) => {
    if (!totalAmountNeeded || !selectedAmount) return <></>;
    return (
        <div className='text-center p-2'>
            <input
                className='form-range'
                type='range'
                defaultValue={defaultValue}
                min={1}
                max={totalAmountNeeded}
                onChange={onChange}
            />
            <div>{calcOilPercentage(selectedAmount, totalAmountNeeded)}</div>
        </div>
    );
};

export const calcOilPercentageInput = (current: number, total: number) => {
    return `${( current * 100 ) / total}% (${current} ml)`;
};
